import React, { useCallback, useEffect, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ActivityIndicator, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../../theme';
import { fcmService } from '../../services/fcm.service';
import { useLocale } from '../../contexts/LocaleContext';
import { useRTLStyles } from '../../hooks/useRTLStyles';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { withAlpha } from '../../theme';

/**
 * Push notification status for this device — each step of the FCM pipeline
 * (permission → APNs token → FCM token → server registration → test send)
 * with the server's actual response, so a release/TestFlight build with no
 * console access can show exactly where delivery breaks. Native only.
 */
export const PushDiagnosticsCard: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const rtl = useRTLStyles();
  const { t } = useLocale();
  const [snapshot, setSnapshot] = useState({ ...fcmService.diagnostics });
  const [busy, setBusy] = useState<'register' | 'test' | null>(null);

  const refresh = useCallback(() => setSnapshot({ ...fcmService.diagnostics }), []);

  useEffect(() => {
    const timer = setInterval(refresh, 2000);
    return () => clearInterval(timer);
  }, [refresh]);

  if (Platform.OS === 'web') return null;

  const reRegister = async () => {
    setBusy('register');
    try {
      await fcmService.initialize();
      await fcmService.registerToken(undefined, true);
    } finally {
      setBusy(null);
      refresh();
    }
  };

  const sendTest = async () => {
    setBusy('test');
    try {
      await fcmService.sendTestNotification();
    } finally {
      setBusy(null);
      refresh();
    }
  };

  const row = (label: string, value: string, ok?: boolean) => (
    <View style={[styles.row, { flexDirection: rtl.rowDirection }]} key={label}>
      <Ionicons
        name={ok === undefined ? 'ellipse-outline' : ok ? 'checkmark-circle' : 'close-circle'}
        size={16}
        color={ok === undefined ? (colors.text.secondary as string) : ok ? (colors.status.success as string) : (colors.status.error as string)}
      />
      <Text style={[styles.label, { textAlign: rtl.textStart }]}>{label}</Text>
      <Text style={[styles.value, { textAlign: rtl.isRTL ? 'left' : 'right' }]} numberOfLines={3}>{value}</Text>
    </View>
  );

  const reg = snapshot.lastRegistration;
  const test = snapshot.lastTest;
  const permissionOk = snapshot.permission === '1' || snapshot.permission === '2';

  return (
    <View style={styles.card}>
      <Text style={[styles.title, { textAlign: rtl.textStart }]}>{t('settings.pushDiagnostics.title')}</Text>
      {row(t('settings.pushDiagnostics.permission'), snapshot.permission ?? '—', snapshot.permission === undefined ? undefined : permissionOk)}
      {Platform.OS === 'ios' && row('APNs', snapshot.apnsToken === undefined ? '—' : snapshot.apnsToken ? 'OK' : '—', snapshot.apnsToken)}
      {row('FCM', snapshot.fcmToken ?? snapshot.initError ?? '—', snapshot.fcmToken ? true : snapshot.initError ? false : undefined)}
      {row(
        t('settings.pushDiagnostics.registration'),
        reg ? `${reg.ok ? 'OK' : `#${reg.statusCode ?? '-'} ${reg.error ?? ''}`} · ${new Date(reg.at).toLocaleTimeString()}` : '—',
        reg ? reg.ok : undefined
      )}
      {row(
        t('settings.pushDiagnostics.test'),
        test ? `${test.ok ? 'OK' : `#${test.statusCode ?? '-'}`} ${test.detail ?? ''}` : '—',
        test ? test.ok : undefined
      )}
      <View style={[styles.actions, { flexDirection: rtl.rowDirection }]}>
        <TouchableOpacity style={styles.button} onPress={reRegister} disabled={!!busy}>
          {busy === 'register' ? <ActivityIndicator size="small" color={colors.primary} /> : <Text style={styles.buttonText}>{t('settings.pushDiagnostics.reRegister')}</Text>}
        </TouchableOpacity>
        <TouchableOpacity style={styles.button} onPress={sendTest} disabled={!!busy}>
          {busy === 'test' ? <ActivityIndicator size="small" color={colors.primary} /> : <Text style={styles.buttonText}>{t('settings.pushDiagnostics.sendTest')}</Text>}
        </TouchableOpacity>
      </View>
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  card: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: spacing.borderRadius.md,
    backgroundColor: colors.background.surface,
    padding: spacing.md,
    marginBottom: spacing.lg,
    gap: spacing.sm,
  },
  title: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
    color: colors.textDark,
  },
  row: {
    alignItems: 'flex-start',
    gap: spacing.sm,
  },
  label: {
    width: 110,
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.textDark,
  },
  value: {
    flex: 1,
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.secondary,
  },
  actions: {
    gap: spacing.sm,
    marginTop: spacing.xs,
  },
  button: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: spacing.sm,
    borderRadius: spacing.borderRadius.sm,
    backgroundColor: withAlpha(colors.primary, 0.08),
  },
  buttonText: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.primary,
  },
});
